/**
 * 深海領域マネージャー
 *
 * 潜航・浮上・深度管理の中核ロジック
 */

import type { CardWithId } from '../../types/card';
import type {
  DeepSeaZone,
  DeepSeaCard,
  SubmergeContext,
  SubmergeResult,
  SurfaceContext,
  SurfaceResult,
  DepthIncrementResult,
  SurfaceBonus,
} from './types';
import {
  DEFAULT_DEEP_SEA_CAPACITY,
  DEFAULT_INITIAL_DEPTH,
  MIN_SURFACE_COST,
  getDepthBonus,
  calculateSurfaceCost,
  SUBMERGIBLE_ATTRIBUTES,
} from './constants';

// ========================================
// 深海領域の初期化・操作
// ========================================

/**
 * 深海領域の初期状態を作成
 */
export function createDeepSeaZone(): DeepSeaZone {
  return {
    cards: [],
    maxCapacity: DEFAULT_DEEP_SEA_CAPACITY,
    isUnlocked: false,
  };
}

/**
 * 深海領域を開放する
 */
export function unlockDeepSeaZone(zone: DeepSeaZone): DeepSeaZone {
  return {
    ...zone,
    isUnlocked: true,
  };
}

/**
 * 深海領域の上限を設定する
 */
export function setDeepSeaCapacity(zone: DeepSeaZone, capacity: number): DeepSeaZone {
  return {
    ...zone,
    maxCapacity: capacity,
  };
}

// ========================================
// 潜航判定・処理
// ========================================

/**
 * カードが潜航可能か判定
 */
export function canSubmerge(
  card: CardWithId | null,
  context: SubmergeContext
): boolean {
  if (!card) return false;

  // モンスターカードのみ潜航可能
  if (card.type !== 'monster') return false;

  // 深海領域が開放されていない
  if (!context.deepSeaZone.isUnlocked) return false;

  // 深海領域が満杯
  if (context.deepSeaZone.cards.length >= context.deepSeaZone.maxCapacity) {
    return false;
  }

  // 【潜航】キーワードを持つカード
  if (hasSenkouKeyword(card)) return true;

  // フェイズカード効果等で水属性カードの潜航を許可
  if (context.allowWaterAttribute && isSubmergibleAttribute(card)) return true;

  return false;
}

/**
 * 【潜航】キーワードを持つか判定
 */
export function hasSenkouKeyword(card: CardWithId | null): boolean {
  if (!card) return false;

  const keywordField = card.keyword;
  if (!keywordField) return false;

  // keyword は string 型（型定義より）
  return keywordField.includes('【潜航】') || keywordField.includes('潜航');
}

/**
 * 潜航可能な属性か判定
 */
export function isSubmergibleAttribute(card: CardWithId | null): boolean {
  if (!card) return false;
  return SUBMERGIBLE_ATTRIBUTES.includes(card.attribute as typeof SUBMERGIBLE_ATTRIBUTES[number]);
}

/**
 * カードを深海領域に潜航させる
 *
 * @param zone 現在の深海領域
 * @param card 潜航するカード
 * @param currentTurn 現在のターン番号
 * @param initialDepth 初期深度（デフォルト1、ヴェルゼファールは2）
 * @returns 潜航結果
 */
export function submergeCard(
  zone: DeepSeaZone,
  card: CardWithId,
  currentTurn: number,
  initialDepth: number = DEFAULT_INITIAL_DEPTH
): SubmergeResult {
  // 開放チェック
  if (!zone.isUnlocked) {
    return {
      success: false,
      updatedZone: zone,
      message: '深海領域が開放されていません',
    };
  }

  // 上限チェック
  if (zone.cards.length >= zone.maxCapacity) {
    return {
      success: false,
      updatedZone: zone,
      message: `深海領域が満杯です（${zone.maxCapacity}枚）`,
    };
  }

  // 深海カードを作成
  const deepSeaCard: DeepSeaCard = {
    card,
    depth: initialDepth,
    submergedTurn: currentTurn,
    uniqueId: card.uniqueId || `deep-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };

  // 深海領域に追加
  const updatedZone: DeepSeaZone = {
    ...zone,
    cards: [...zone.cards, deepSeaCard],
  };

  return {
    success: true,
    updatedZone,
    message: `${card.name}が深海へと潜航した！（深度${initialDepth}）`,
  };
}

// ========================================
// 浮上判定・処理
// ========================================

/**
 * 深海カードが浮上可能か判定
 */
export function canSurface(
  deepSeaCard: DeepSeaCard | null,
  context: SurfaceContext
): boolean {
  if (!deepSeaCard) return false;

  // フィールドに空きがない
  if (context.emptyFieldSlots <= 0) return false;

  // 浮上コスト計算
  const cost = calculateSurfaceCost(
    deepSeaCard.card.cost || 0,
    deepSeaCard.depth,
    context.costReduction
  );

  // SP不足
  if (context.activeSP < cost) return false;

  return true;
}

/**
 * 浮上コストを取得（UI表示用）
 */
export function getSurfaceCost(
  deepSeaCard: DeepSeaCard,
  costReduction: number = 0
): number {
  return calculateSurfaceCost(
    deepSeaCard.card.cost || 0,
    deepSeaCard.depth,
    costReduction
  );
}

/**
 * カードを深海領域から浮上させる
 *
 * @param zone 現在の深海領域
 * @param deepSeaIndex 浮上するカードのインデックス
 * @param costReduction コスト軽減量
 * @returns 浮上結果
 */
export function surfaceCard(
  zone: DeepSeaZone,
  deepSeaIndex: number,
  costReduction: number = 0
): SurfaceResult {
  // インデックス範囲チェック
  if (deepSeaIndex < 0 || deepSeaIndex >= zone.cards.length) {
    return {
      success: false,
      updatedZone: zone,
      surfacedCard: null,
      surfaceDepth: 0,
      bonus: getDepthBonus(0),
      spCost: 0,
      message: '無効なカードインデックスです',
    };
  }

  const deepSeaCard = zone.cards[deepSeaIndex];
  const depth = deepSeaCard.depth;
  const bonus = getDepthBonus(depth);
  const spCost = calculateSurfaceCost(
    deepSeaCard.card.cost || 0,
    depth,
    costReduction
  );

  // 深海領域からカードを削除
  const updatedCards = zone.cards.filter((_, idx) => idx !== deepSeaIndex);
  const updatedZone: DeepSeaZone = {
    ...zone,
    cards: updatedCards,
  };

  // ボーナスメッセージを構築
  const bonusMessages: string[] = [];
  if (bonus.attackBonus > 0) {
    bonusMessages.push(`ATK+${bonus.attackBonus}`);
  }
  if (bonus.canPreemptiveStrike) {
    bonusMessages.push('先制攻撃');
  }
  const bonusText = bonusMessages.length > 0 ? ` (${bonusMessages.join(', ')})` : '';

  return {
    success: true,
    updatedZone,
    surfacedCard: deepSeaCard.card,
    surfaceDepth: depth,
    bonus,
    spCost,
    message: `${deepSeaCard.card.name}が深海から浮上！深度${depth}${bonusText}`,
  };
}

/**
 * 強制浮上（対策カード効果用）
 * 深度0扱いでコストも払えない場合は破壊
 *
 * @param zone 現在の深海領域
 * @param deepSeaIndex 浮上するカードのインデックス
 * @param ignoreDepth 深度を無視するか（0扱い）
 * @returns 浮上結果（成功=浮上、失敗=破壊）
 */
export function forceSurfaceCard(
  zone: DeepSeaZone,
  deepSeaIndex: number,
  ignoreDepth: boolean = true
): SurfaceResult {
  if (deepSeaIndex < 0 || deepSeaIndex >= zone.cards.length) {
    return {
      success: false,
      updatedZone: zone,
      surfacedCard: null,
      surfaceDepth: 0,
      bonus: getDepthBonus(0),
      spCost: 0,
      message: '無効なカードインデックスです',
    };
  }

  const deepSeaCard = zone.cards[deepSeaIndex];
  const effectiveDepth = ignoreDepth ? 0 : deepSeaCard.depth;
  const spCost = calculateSurfaceCost(deepSeaCard.card.cost || 0, effectiveDepth, 0);

  // 深海領域からカードを削除
  const updatedCards = zone.cards.filter((_, idx) => idx !== deepSeaIndex);
  const updatedZone: DeepSeaZone = {
    ...zone,
    cards: updatedCards,
  };

  return {
    success: true,
    updatedZone,
    surfacedCard: deepSeaCard.card,
    surfaceDepth: effectiveDepth,
    bonus: getDepthBonus(effectiveDepth),
    spCost,
    message: `${deepSeaCard.card.name}が強制浮上！（深度${effectiveDepth}扱い、コスト${spCost}）`,
  };
}

// ========================================
// 深度管理
// ========================================

/**
 * 深海領域の全カードの深度を増加させる
 *
 * @param zone 現在の深海領域
 * @param bonusDepth 追加の深度増加（暗黒水域、フィールドカード効果等）
 * @returns 深度増加結果
 */
export function incrementDepth(
  zone: DeepSeaZone,
  bonusDepth: number = 0
): DepthIncrementResult {
  if (zone.cards.length === 0) {
    return {
      updatedZone: zone,
      incrementedCount: 0,
      bonusDepth,
    };
  }

  const totalIncrement = 1 + bonusDepth;

  const updatedCards = zone.cards.map(deepSeaCard => ({
    ...deepSeaCard,
    depth: deepSeaCard.depth + totalIncrement,
  }));

  return {
    updatedZone: {
      ...zone,
      cards: updatedCards,
    },
    incrementedCount: updatedCards.length,
    bonusDepth,
  };
}

/**
 * 特定のカードの深度を増加させる
 */
export function incrementCardDepth(
  zone: DeepSeaZone,
  deepSeaIndex: number,
  amount: number = 1
): DeepSeaZone {
  if (deepSeaIndex < 0 || deepSeaIndex >= zone.cards.length) {
    return zone;
  }

  const updatedCards = zone.cards.map((card, idx) => {
    if (idx === deepSeaIndex) {
      return { ...card, depth: card.depth + amount };
    }
    return card;
  });

  return {
    ...zone,
    cards: updatedCards,
  };
}

// ========================================
// 破壊処理
// ========================================

/**
 * 深海領域のカードを破壊する
 */
export function destroyDeepSeaCard(
  zone: DeepSeaZone,
  deepSeaIndex: number
): { updatedZone: DeepSeaZone; destroyedCard: CardWithId | null } {
  if (deepSeaIndex < 0 || deepSeaIndex >= zone.cards.length) {
    return { updatedZone: zone, destroyedCard: null };
  }

  const destroyedCard = zone.cards[deepSeaIndex].card;
  const updatedCards = zone.cards.filter((_, idx) => idx !== deepSeaIndex);

  return {
    updatedZone: {
      ...zone,
      cards: updatedCards,
    },
    destroyedCard,
  };
}

/**
 * 深海領域の全カードを破壊する
 */
export function destroyAllDeepSeaCards(
  zone: DeepSeaZone
): { updatedZone: DeepSeaZone; destroyedCards: CardWithId[] } {
  const destroyedCards = zone.cards.map(dsc => dsc.card);

  return {
    updatedZone: {
      ...zone,
      cards: [],
    },
    destroyedCards,
  };
}

// ========================================
// ユーティリティ
// ========================================

/**
 * 深海領域にカードがあるか
 */
export function hasCardsInDeepSea(zone: DeepSeaZone): boolean {
  return zone.cards.length > 0;
}

/**
 * 深海領域のカード数を取得
 */
export function getDeepSeaCardCount(zone: DeepSeaZone): number {
  return zone.cards.length;
}

/**
 * 深海領域に空きがあるか
 */
export function hasDeepSeaCapacity(zone: DeepSeaZone): boolean {
  return zone.isUnlocked && zone.cards.length < zone.maxCapacity;
}

/**
 * 特定の深度以上のカードを取得
 */
export function getCardsWithMinDepth(zone: DeepSeaZone, minDepth: number): DeepSeaCard[] {
  return zone.cards.filter(card => card.depth >= minDepth);
}

/**
 * 浮上可能なカードを取得
 */
export function getSurfaceableCards(
  zone: DeepSeaZone,
  activeSP: number,
  emptyFieldSlots: number,
  costReduction: number = 0
): DeepSeaCard[] {
  const context: SurfaceContext = {
    deepSeaZone: zone,
    activeSP,
    emptyFieldSlots,
    costReduction,
  };

  return zone.cards.filter(card => canSurface(card, context));
}

/**
 * 【海底の眠り】を持つカードが深海領域にあるか
 */
export function hasKaiteiNoNemuriInDeepSea(zone: DeepSeaZone): boolean {
  return zone.cards.some(deepSeaCard => {
    const keyword = deepSeaCard.card.keyword;
    if (!keyword) return false;
    // keyword は string 型（型定義より）
    return keyword.includes('【海底の眠り】') || keyword.includes('海底の眠り');
  });
}

/**
 * 【暗黒水域】を持つモンスターがフィールドにいるかチェック用ヘルパー
 * （実際のチェックはmagic-spirit.tsx側で行う）
 */
export function hasAnkokuSuiikiKeyword(card: CardWithId | null): boolean {
  if (!card) return false;

  const keyword = card.keyword;
  if (!keyword) return false;

  // keyword は string 型（型定義より）
  return keyword.includes('【暗黒水域】') || keyword.includes('暗黒水域');
}
